/**
 * Image-Builds API Service.
 *
 * Phase 2: read-only (list + getById).
 * Phase 3: dispatch (build) — operator triggers, backend quotas + dispatches.
 * Phase 4: admin promote (promote) — flags an existing staging version as production.
 */

import apiClient from './api';
import { ApiResponse, ImageBuildDoc, ImageUsageResult } from './types';

export interface DispatchedBuild {
  buildId: string;
  jobUrl: string | null;
  status: 'running' | 'queued';
  message: string;
}

export interface PromotedBuild {
  buildId: string;
  jobUrl: string | null;
  promotedAt: string;
  message: string;
}

export const imageBuildsApi = {
  /**
   * GET /api/images?workloadId=
   * List image-builds; backend applies operator-role + per-tenant + per-user
   * filtering (D3). Pass `workloadId` to scope by a single workload.
   */
  async list(params?: { workloadId?: string }): Promise<ImageBuildDoc[]> {
    const response = await apiClient.get<ApiResponse<ImageBuildDoc[]>>('/images', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch image builds');
    }
    return response.data.data;
  },

  /**
   * GET /api/image-usage?workloadId=
   * NC-1295 — run + stability figures per image version, derived from run
   * documents. Own endpoint rather than a field on /api/images: that response is
   * user-scoped, while these figures are plant-wide facts about a version.
   */
  async usage(params?: { workloadId?: string }): Promise<ImageUsageResult> {
    const response = await apiClient.get<ApiResponse<ImageUsageResult>>('/image-usage', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch image usage');
    }
    return response.data.data;
  },

  /**
   * GET /api/images/{buildId}
   * Read a single image-build. `workloadIdHint` makes the backend use a
   * point-read on the partition key — pass it whenever the caller already
   * knows the workload (it always does, when navigated from the list).
   */
  async getById(buildId: string, workloadIdHint?: string): Promise<ImageBuildDoc> {
    const response = await apiClient.get<ApiResponse<ImageBuildDoc>>(
      `/images/${buildId}`,
      { params: workloadIdHint ? { workloadId: workloadIdHint } : undefined }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch image build');
    }
    return response.data.data;
  },

  /**
   * POST /api/images/build
   * NC-498 Phase 3 — operator-triggered Tier-3 staging build. The backend
   * hard-codes tier/environment and quotas the caller; this wrapper ferries
   * `workloadId`, the optional dry-run flag, and (NC-867 T5) the mandatory owner
   * cost-center + optional purpose captured on the build's audit doc.
   */
  async build(input: {
    workloadId: string;
    dryRun?: boolean;
    ownerCostCenter: string;
    purpose?: string;
  }): Promise<DispatchedBuild> {
    const response = await apiClient.post<ApiResponse<DispatchedBuild>>(
      '/images/build',
      input
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to dispatch image build');
    }
    return response.data.data;
  },

  /**
   * POST /api/images/{buildId}/promote
   * NC-498 Phase 4 — admin-only. Promotes a completed staging build to
   * production by tagging the gallery image-version. Pass `workloadIdHint`
   * so the backend reads via point-read on the partition key.
   */
  async promote(buildId: string, workloadIdHint?: string): Promise<PromotedBuild> {
    const response = await apiClient.post<ApiResponse<PromotedBuild>>(
      `/images/${buildId}/promote`,
      undefined,
      { params: workloadIdHint ? { workloadId: workloadIdHint } : undefined }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to promote image build');
    }
    return response.data.data;
  },
};
